import Vue from 'vue';
import router from '../router';

function getSafe(obj, path, defaultValue) {
    try {
        return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj) ?? defaultValue;
    } catch (_) {
        return defaultValue;
    }
}

function toByteLength(value) {
    try {
        const str = typeof value === 'string' ? value : JSON.stringify(value);
        return str ? str.length : 0;
    } catch (_) {
        return 0;
    }
}

function maskHeaders(headers) {
    if (!headers) return {};
    const lower = {};
    Object.keys(headers).forEach((k) => (lower[k.toLowerCase()] = headers[k]));
    const masked = { ...lower };
    if (masked['authorization']) masked['authorization'] = '***';
    if (masked['cookie']) masked['cookie'] = '***';
    if (masked['x-api-key']) masked['x-api-key'] = '***';
    return masked;
}

function extractTraceId(error) {
    const response = error && error.response;
    const data = response && response.data ? response.data : {};
    const headers = response && response.headers ? response.headers : {};
    return (
        data.traceId ||
        data.requestId ||
        data.trace_id ||
        data.request_id ||
        headers['x-request-id'] ||
        headers['x-trace-id'] ||
        null
    );
}

const ErrorService = {
    normalize(error, config = {}) {
        const isNetworkError = !!(error && error.request && !error.response);
        const response = error && error.response;
        const data = response && response.data ? response.data : {};

        const status = response ? response.status : 0;
        const code = data && (data.code || data.error || data.name) ? (data.code || data.error || data.name) : (error && error.code) || undefined;
        const apiMessage = (data && (data.message || data.error_description || data.error)) || error.message || 'Unknown error';
        const details = data && (data.errors || data.details) ? (data.errors || data.details) : undefined;
        const traceId = extractTraceId(error);

        const method = (config && config.method ? String(config.method).toUpperCase() : undefined) || 'GET';
        const url = (config && config.url) || undefined;
        const requestHeaders = config && config.headers ? maskHeaders(config.headers) : {};
        const payloadSize = toByteLength(config && config.data);
        const responseSize = toByteLength(response && response.data);
        const network = typeof navigator !== 'undefined' ? (navigator.onLine ? 'online' : 'offline') : 'unknown';
        const appVersion = process.env.VUE_APP_VERSION || process.env.VUE_APP_BUILD || 'unknown';
        let userId = undefined;
        try {
            const profile = JSON.parse(localStorage.getItem('profile'));
            userId = profile && profile.id ? profile.id : undefined;
        } catch (_) {}

        const context = {
            route: router && router.currentRoute ? router.currentRoute.fullPath : undefined,
            action: config && config.__action ? config.__action : `${method} ${url || ''}`.trim(),
            request: { method, url, headers: requestHeaders },
            payloadSize,
            responseSize
        };

        const normalized = {
            status: isNetworkError && (error && error.code) === 'ECONNABORTED' ? 0 : status,
            code,
            apiMessage,
            details,
            traceId,
            context,
            timestamp: new Date().toISOString(),
            userId,
            network,
            appVersion,
            featureFlag: undefined,
            stack: error && error.stack ? error.stack : undefined
        };

        return normalized;
    },

    buildFieldErrorsMap(details) {
        const result = {};
        if (!details) return result;
        // details может быть:
        // 1) [{ field, message }] или [{ path, message }]
        // 2) { field: [messages] } или { field: 'message' }
        // 3) nested объекты
        const pushMsg = (field, message) => {
            if (!field || !message) return;
            if (!result[field]) result[field] = [];
            result[field].push(String(message));
        };

        if (Array.isArray(details)) {
            details.forEach((item) => {
                if (!item) return;
                const field = item.field || item.path || item.param || item.key;
                const message = item.message || item.msg || item.error || item.text;
                pushMsg(field, message);
            });
        } else if (typeof details === 'object') {
            Object.keys(details).forEach((key) => {
                const val = details[key];
                if (Array.isArray(val)) {
                    val.forEach((msg) => pushMsg(key, msg));
                } else if (val && typeof val === 'object') {
                    // вложенные поля: key.subkey
                    Object.keys(val).forEach((subKey) => {
                        const subVal = val[subKey];
                        if (Array.isArray(subVal)) {
                            subVal.forEach((msg) => pushMsg(`${key}.${subKey}`, msg));
                        } else if (typeof subVal === 'string') {
                            pushMsg(`${key}.${subKey}`, subVal);
                        }
                    });
                } else if (typeof val === 'string') {
                    pushMsg(key, val);
                }
            });
        }

        // Вернем карту field -> string (первая ошибка)
        const flat = {};
        Object.keys(result).forEach((k) => {
            flat[k] = result[k][0];
        });
        return flat;
    },

    formatUserMessage(normalized) {
        const status = normalized && normalized.status;
        const apiMessage = normalized && normalized.apiMessage ? normalized.apiMessage : 'Ошибка';
        const traceSuffix = normalized && normalized.traceId ? ` (traceId: ${normalized.traceId})` : '';

        const hasValidationDetails = normalized && normalized.details && Object.keys(normalized.details || {}).length > 0;
        let text;
        if (!status || status === 0) {
            text = 'Нет соединения или истек таймаут. Проверьте сеть и попробуйте снова.';
        } else if ((status === 400 || status === 422) && hasValidationDetails) {
            text = 'Исправьте ошибки в форме. Проверьте обязательные поля и формат данных.';
        } else if (status === 401) {
            text = 'Авторизуйтесь заново. Возможно, сессия истекла.';
        } else if (status === 403) {
            text = 'Недостаточно прав для этого действия. Обратитесь к администратору прав.';
        } else if (status === 404) {
            text = 'Ресурс не найден. Проверьте фильтры/ID/доступность.';
        } else if (status === 429) {
            text = 'Слишком много запросов. Повторите позже.';
        } else if (status >= 500) {
            text = 'Проблема на стороне сервера. Повторите попытку позже или обратитесь в поддержку.';
        } else {
            // 400 без деталей валидации и прочие случаи — непредвиденная/техническая ошибка
            text = 'Непредвиденная ошибка. Попробуйте обновить страницу или повторить действие.';
        }

        return `${text} (API: ${apiMessage})${traceSuffix}`;
    },

    show(normalized, options = {}) {
        // Используем существующий глобальный алерт через event-bus
        const message = this.formatUserMessage(normalized);
        if (!options || !options.silent) {
            if (Vue.prototype.$root && Vue.prototype.$root.$emit) {
                Vue.prototype.$root.$emit('showError', message);
            } else if (typeof window !== 'undefined') {
                // Fallback
                // eslint-disable-next-line no-alert
                console.error(message);
            }
        }
    },

    log(normalized) {
        // Централизованное логирование; можно заменить на Sentry/внутренний endpoint
        try {
            // Не логируем персональные данные; отправляем только метаданные
            const logEntry = {
                timestamp: normalized.timestamp,
                userId: normalized.userId,
                route: normalized.context && normalized.context.route,
                action: normalized.context && normalized.context.action,
                request: normalized.context && normalized.context.request,
                status: normalized.status,
                code: normalized.code,
                apiMessage: normalized.apiMessage,
                traceId: normalized.traceId,
                details: normalized.details,
                payloadSize: normalized.context && normalized.context.payloadSize,
                responseSize: normalized.context && normalized.context.responseSize,
                network: normalized.network,
                appVersion: normalized.appVersion,
                featureFlag: normalized.featureFlag,
                stack: normalized.stack
            };
            // Пока логируем в консоль; интеграция со сторонним логгером может быть добавлена здесь
            // eslint-disable-next-line no-console
            console.error('[ClientError]', logEntry);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Не удалось залогировать ошибку', e);
        }
    }
};

export default ErrorService;


